/**
 * מסחרי וחיוב — the commercial home of the control plane.
 *
 * ── WHY THIS SCREEN IS MOSTLY A LIST AND PARTLY A REFUSAL ───────────────────────────────────────
 *
 * The commercial acts in this product are all PER CUSTOMER and all already exist: plan assignment,
 * SKUs, trial dates, subscription state, discounts and their second-person approval, each on
 * `/platform/tenants/{uid}/commercial` and its subscription sibling. What did NOT exist was a place
 * to stand and see the commercial position of the whole book at once, which is the first question
 * an Owner asks and the last one a per-customer screen can answer.
 *
 * So this screen does two things and refuses a third:
 *
 *   · IT COUNTS. Every commercial state across every customer, from `platform.tenant.read`, with
 *     the customers in each state reachable in one click.
 *   · IT ROUTES. Each row leads to that customer's commercial screen, which is where money is
 *     actually changed.
 *   · IT REFUSES TO SHOW REVENUE. No read in this product returns an amount -- 08 §8.4 / CF-66 keep
 *     money out of entitlement, and `TenantAdminSummary` carries no price by design. A revenue tile
 *     here would be a number nothing produced, and ₪0 on a platform that cannot compute revenue is
 *     a FALSE statement rather than an empty one.
 *
 * 09 §5.8: every state is written out. The empty state here is "no customers", which is a state a
 * platform can legitimately be in and is not an error.
 */

import { useCallback } from 'react';

import { AppShell } from '../shell/AppShell';
import { useWorkContext } from '../shell/ContextProvider';
import { DataTable } from '../components/DataTable';
import { ServerRead } from '../components/ServerRead';
import { StatusChip } from '../components/StatusChip';
import { SurfaceNotice } from '../components/SurfaceNotice';
import type { Column } from '../components/DataTable';
import type { TenantAdminSummary } from '../api/contract';
import { subscriptionStateHe, subscriptionStateOrNoneHe } from '../api/adminVocabulary';
import { callOperation } from '../api/operations';
import { useServerData } from '../api/useServerData';
import { summariseEstate } from '../api/estateSummary';
import { routes } from '../routes';

const TITLE_HE = 'מסחרי וחיוב';

export function PlatformBillingScreen(): JSX.Element {
  const ctx = useWorkContext();

  const read = useCallback(() => callOperation('platform.tenant.read', {}), []);
  const tenantsRead = useServerData(read);

  /* The money, from the one read that measures it. Its own `ServerRead`: the commercial states
     arriving while the revenue did not is a real state, and blanking both would discard a
     measurement that succeeded. */
  const readMetrics = useCallback(() => callOperation('platform.metrics.read', {}), []);
  const metricsRead = useServerData(readMetrics);


  if (ctx.surface !== 'PLATFORM') {
    return <SurfaceNotice titleHe={TITLE_HE} requires="PLATFORM" actual={ctx.surface} />;
  }

  const columns: readonly Column<TenantAdminSummary>[] = [
    {
      key: 'name',
      headerHe: 'לקוח',
      render: (t) => (
        <span className="company-cell">
          <span className="company-cell__name">{t.legalName}</span>
          <span className="company-cell__id" dir="ltr">
            {t.slug}
          </span>
        </span>
      ),
    },
    {
      key: 'subscription',
      headerHe: 'מצב המנוי',
      render: (t) => {
        const term = subscriptionStateOrNoneHe(t.subscriptionState);
        return <StatusChip labelHe={term.labelHe} tone={term.tone} />;
      },
    },
    {
      key: 'companies',
      headerHe: 'חברות',
      numeric: true,
      render: (t) => <span dir="ltr">{t.companyCount}</span>,
    },
    {
      key: 'seats',
      headerHe: 'משתמשים',
      numeric: true,
      render: (t) => <span dir="ltr">{t.seatCount}</span>,
    },
  ];

  return (
    <AppShell
      titleHe={TITLE_HE}
      backToParent={{ labelHe: 'קונסולת הפלטפורמה', href: routes.platformConsole }}
      /*
       * THE PROVENANCE LINE CONTRADICTED THE SCREEN. **F1B.1.**
       *
       * It said "אין במסך הזה סכום כספי כלשהו" — there is no monetary amount on this screen — and
       * it was true when it was written, before any read in the product returned one. F1R-05 put
       * three amounts on the page and left the sentence underneath them.
       *
       * A provenance line is where an Owner goes to find out whether to trust what they are
       * reading, so a stale one is worse than none: it teaches them the line is decoration. It now
       * says what the amounts ARE — what was agreed in the subscription rows — and what they are
       * NOT, which is money received. In business words, because that is who reads it.
       */
    >
      <ServerRead result={tenantsRead.result} whatHe="את המצב המסחרי" skeletonRows={6}>
        {(data) => {
          const summary = summariseEstate(data.tenants);
          return (
            <>
              <section className="figures" aria-label="פילוח מסחרי">
                {summary.bySubscriptionState.map((b) => {
                  const term = subscriptionStateHe(b.code);
                  return (
                    <article key={b.code} className="figure">
                      <p className="figure__label">{term.labelHe}</p>
                      <p className="figure__value" dir="ltr">
                        {b.count}
                      </p>
                    </article>
                  );
                })}
              </section>

              <DataTable<TenantAdminSummary>
                captionHe="לקוחות לפי מצב מנוי"
                columns={columns}
                rows={data.tenants}
                rowKey={(t) => t.tenantUid}
                childHref={(t) => routes.platformTenantCommercial(t.tenantUid)}
                filterSummaryHe={`מציג ${data.tenants.length} לקוחות — כל הלקוחות שהפלטפורמה מנהלת`}
                emptyStateHe="עדיין אין לקוחות, ולכן אין מצב מסחרי להציג. זו התשובה של השרת, לא תקלה."
              />
            </>
          );
        }}
      </ServerRead>

      {/*
        THE MONEY, MEASURED. This block said "MRR — not available, and here is the missing read",
        which was true and is no longer: `platform.metrics.read` computes it in the database from
        the price snapshots on live SKU rows. Amounts are STRINGS and are rendered as strings.
      */}
      <ServerRead result={metricsRead.result} whatHe="את הסכומים החוזים" skeletonRows={2}>
        {(data) => (
          <>
            {/*
              THE FIGURE, UNDER ITS TRUE NAME. F1R-05.
              This was three tiles: "הכנסה חודשית חוזרת", "הכנסה שנתית חוזרת" and a churn figure
              described as MRR. None applied a discount and none normalised a billing period, so a
              customer on a 100% waiver appeared as paying revenue.
            */}
            <section className="figures" aria-label="סכומים חוזים">
              <article className="figure">
                <p className="figure__label">סכום המחירים שנקבעו במנויים פעילים</p>
                <p className="figure__value" dir="ltr">
                  {data.metrics.contractedSnapshotTotal}
                </p>
                <p className="figure__note">לפני הנחות, ללא תרגום לתקופת חיוב</p>
              </article>
              <article className="figure">
                <p className="figure__label">סכום שנקבע במנויים שננטשו</p>
                <p className="figure__value" dir="ltr">
                  {data.metrics.churnedSnapshotTotal}
                </p>
                <p className="figure__note">ב-30 הימים האחרונים</p>
              </article>
              <article className="figure">
                <p className="figure__label">מנויים פעילים עם הנחה</p>
                <p className="figure__value" dir="ltr">
                  {data.metrics.discountedSubscriptions}
                </p>
                <p className="figure__note">הסכומים שלמעלה אינם מפחיתים אותן</p>
              </article>
            </section>

            <section className="not-available" aria-label="הכנסה חוזרת">
              <h3 className="breakdown__title">הכנסה חוזרת</h3>
              <dl className="not-available__list">
                <div className="not-available__item">
                  <dt>
                    הכנסה חודשית ושנתית —{' '}
                    <span className="not-available__flag">טרם נבנה מנוע החיוב</span>
                  </dt>
                  <dd>
                    הסכומים שלמעלה הם מה שסוכם בשורות המנוי, ולא מה שנכנס בפועל. כדי להפוך אותם
                    להכנסה חוזרת צריך להפחית הנחות, לתרגם מנוי שנתי או רבעוני לחודש, ולחשב חלוקה
                    יחסית לתקופות חלקיות — שלושתם חלק ממנוע החיוב שטרם נבנה. עד אז המערכת מציגה את
                    מה שהיא באמת יודעת, ולא מספר שנראה כמו הכנסה.
                  </dd>
                </div>
              </dl>
            </section>
          </>
        )}
      </ServerRead>

      {/*
        AND WHETHER THE FIGURES ABOVE ARE COMPLETE. FND-09.
        A line sold without a price is excluded from the sum — correctly, because a missing price is
        not a price of zero. What must not happen is the exclusion being silent: an Owner reading a
        revenue figure is entitled to know it measures the whole book and not part of it.
      */}
      <ServerRead result={metricsRead.result} whatHe="את שלמות הנתונים" skeletonRows={1}>
        {(data) =>
          data.metrics.unpricedLines === 0 ? (
            <p className="tile__lead">
              כל שורות המנוי הפעילות נושאות מחיר, ולכן הסכומים שלמעלה מכסים את מלוא ספר הלקוחות.
            </p>
          ) : (
            <section className="not-available" aria-label="שלמות הנתונים">
              <h3 className="breakdown__title">הסכומים שלמעלה חלקיים</h3>
              <dl className="not-available__list">
                <div className="not-available__item">
                  <dt>
                    שורות מנוי ללא מחיר —{' '}
                    <span className="not-available__flag" dir="ltr">
                      {data.metrics.unpricedLines}
                    </span>
                  </dt>
                  <dd>
                    שורות אלה נמכרו ללא מחיר שנשמר, ולכן אינן נכללות בסכומים. הן אינן נספרות כאפס:
                    אפס היה אומר שנמכרו בחינם. כדי לכלול אותן יש לוודא שספר המחירים הפעיל מתמחר את
                    כל הפריטים שנמכרו, ואז להוסיף את הפריט מחדש ללקוח.
                  </dd>
                </div>
              </dl>
            </section>
          )
        }
      </ServerRead>

      {/*
        AND THE PROVIDER, IN THE LANGUAGE OF THE BUSINESS.
        Not "NOT_CONFIGURED" and not a table name: what the Owner needs to know is that GL-POINT has
        not been connected to a payment provider, so nothing has been charged and no payment can
        have failed. That is a true and complete statement about the business, and it is why there
        is no aging list rather than an aging list showing zero.
      */}
      {/*
        ── THE CHARGES ARE NOT HERE, AND THAT IS THE POINT. **Owner, 2026-11-02.** ───────────────

        *"זה צריך להיות בנפרד ואל תכניס את הבילינג לתוך מנויים ומכסות, זה בנפרד."* They were put on
        this screen for about an hour and it was the second wrong home in a row: this screen answers
        *"what was sold and on what terms"*, and a charge answers *"what moved, to whom, and did it
        work"*. מרכז חיוב is the destination that already existed for the second question.

        What stays here is the commercial picture — the book, the states, the agreed sums — which is
        what מנויים ומכסות is for.
      */}
      <section className="not-available" aria-label="גבייה">
        <h3 className="breakdown__title">גבייה בפועל</h3>
        <dl className="not-available__list">
          <div className="not-available__item">
            <dt>החיובים, הכשלים והמסמכים — במרכז החיוב</dt>
            <dd>
              כל מה שמעל הוא מצב מסחרי: מה נמכר, באיזה מחיר ובאיזה מצב מנוי. החיובים בפועל של כל
              הלקוחות, הכשלים והמסמכים שיצאו נמצאים במסך נפרד — מרכז חיוב — כדי שלא יתערבבו עם
              מה שסוכם.
            </dd>
          </div>
        </dl>
      </section>
    </AppShell>
  );
}
